import { accommodationFactory } from "@@/shared/acquire/accommodation";
import type { AccommodationInventory } from "@@/shared/acquire/accommodation-inventory";
import { accommodationInventoryFactory } from "@@/shared/acquire/accommodation-inventory";
import type { AccommodationRate } from "@@/shared/acquire/accommodation-rate";
import { accommodationRateFactory } from "@@/shared/acquire/accommodation-rate";
import { accommodationConditionDuplicateFactory } from "@@/shared/acquire/accommodation-rate-conditions";
import { accommodationRateOptionFactory } from "@@/shared/acquire/accommodation-rate-option";
import type { Acquirable } from "../acquire/acquirable";
import { bookableFactory } from "../acquire/bookable";
import { buyableFactory } from "../acquire/buyable";
import { contentBlockFactory } from "../acquire/content-block";
import type { ExtrasGroup } from "../acquire/extras-group";
import { extrasGroupsFactory } from "../acquire/extras-group";
import { isBookable } from "../acquire/is-bookable";
import { isBuyable } from "../acquire/is-buyable";
import { isVoucher } from "../acquire/is-voucher";
import { quantityPickerBookableOptionFactory } from "../acquire/resource-reference-options/quantity-picker-bookable-option";
import type { ResourceReference } from "../acquire/resource-references/resource-reference";
import { resourceReferenceFactory } from "../acquire/resource-references/resource-reference";
import type { ResourceReferenceGroup } from "../acquire/resource-references/resource-reference-group";
import { resourceReferenceGroupFactory } from "../acquire/resource-references/resource-reference-group";
import type { ResourceGroup } from "../acquire/resources/resource-group";
import { resourceIdFactory } from "../acquire/resources/resource-id";
import { voucherFactory } from "../acquire/voucher";
import { assertNever } from "../assert-never";
import { now } from "../date-time/date-time";
import {
    type MetaContent,
    metaContentFactory,
} from "../meta-content/meta-content";
import { priceFactory } from "../prices/price";
import type { ProviderId } from "../providers/provider-id";
import { alfredItemStatusFactory } from "../sections/alfred-item-status";
import type { Translatable } from "../translations/translation";
import { translation } from "../translations/translation";
import type { Product } from "./product";
import { productFactory } from "./product";
import type { ProductId } from "./product-id";
import { productIdFactory } from "./product-id";
import type {
    AccommodationSku,
    BookableSku,
    BuyableSku,
    GiftCardSku,
    Sku,
    VoucherSku,
} from "./sku";
import { skuFactory } from "./sku";

const duplicateMetaContent = (meta: MetaContent): MetaContent => {
    return metaContentFactory({
        title: meta.title,
        description: meta.description,
        actionText: meta.actionText,
        background: meta.background,
        images: meta.images,
        tags: meta.tags,
        kicker: meta.kicker,
        subtitle: meta.subtitle,
    });
};

const duplicateRReferenceGroup = (
    ref: ResourceReferenceGroup,
): ResourceReferenceGroup => {
    const list = ref.references.map(r => duplicateReference(r));
    return resourceReferenceGroupFactory({
        references: list,
        meta: ref.meta,
        unit: ref.unit,
    });
};

const duplicateReference = (ref: ResourceReference): ResourceReference => {
    const newResource =
        ref.resource._type === "RESOURCE_GROUP"
            ? ({
                  ...ref.resource,
                  resources: [...ref.resource.resources],
                  meta: metaContentFactory({
                      title: ref.resource.meta.title,
                  }),
                  _id: resourceIdFactory(),
              } as ResourceGroup)
            : {
                  ...ref.resource,
                  _id: resourceIdFactory(),
              };
    const newRef = resourceReferenceFactory({
        meta: ref.meta,

        options: [],
        resource: {
            ...newResource,
        },
        ...(ref.schedulingResource
            ? { schedulingResource: ref.schedulingResource }
            : {}),
        ...(ref.minMax ? { minMax: ref.minMax } : {}),
        ...(ref.resourcePickerEnabled
            ? { resourcePickerEnabled: ref.resourcePickerEnabled }
            : {}),
    });

    const options = ref.options.flatMap(o => {
        if (o._type === "QUANTITY_PICKER_BOOKABLE_OPTION") {
            return quantityPickerBookableOptionFactory({
                ...o,
                resourceId: newRef.resource._id,
                resourceReferenceId: newRef._id,
                _id: undefined,
            });
        }
        return [];
    });
    const updatedNewRef = {
        ...newRef,
        options: options,
    };
    return updatedNewRef;
};

const duplicateAcquirable = (acquirable: Acquirable): Acquirable => {
    if (isBuyable(acquirable)) {
        return buyableFactory({
            price: priceFactory(acquirable.price),
            amount: acquirable.amount,
            minQuantity: acquirable.minQuantity,
        });
    }

    if (isBookable(acquirable)) {
        return bookableFactory({
            hasAccess: acquirable.hasAccess,
            settings: {
                ...acquirable.settings,
            },
            main:
                acquirable.main._type === "RESOURCE_REFERENCE"
                    ? duplicateReference(acquirable.main)
                    : duplicateRReferenceGroup(acquirable.main),
            resources: acquirable.resources.map(r => {
                return r._type === "RESOURCE_REFERENCE"
                    ? duplicateReference(r)
                    : duplicateRReferenceGroup(r);
            }),
            extras: [...acquirable.extras],
            extrasGroups:
                acquirable.extrasGroups?.map(duplicateExtrasGroup) ?? [],
            sessions: undefined,
            contentBlocks: acquirable.contentBlocks.map(block => {
                return contentBlockFactory({
                    content: block.content,
                    title: block.title,
                    link: block.link,
                });
            }),
            price: priceFactory(acquirable.price),
            amount: acquirable.amount,
            type: acquirable.type ?? "GENERIC",

            ...(acquirable.type === "TABLE_BOOKING"
                ? {
                      ...(acquirable.maxGuestsPerSession
                          ? {
                                maxGuestsPerSession:
                                    acquirable.maxGuestsPerSession,
                            }
                          : {}),

                      ...(acquirable.cancellationByCustomerTimeLimit
                          ? {
                                cancellationByCustomerTimeLimit:
                                    acquirable.cancellationByCustomerTimeLimit,
                            }
                          : {}),
                      ...(acquirable.cancellationByCustomerAllowed
                          ? {
                                cancellationByCustomerAllowed:
                                    acquirable.cancellationByCustomerAllowed,
                            }
                          : {}),
                  }
                : {}),
            ...(acquirable.type === "RENTAL"
                ? {
                      spanning: acquirable.spanning ? true : false,
                  }
                : {}),
        });
    }

    if (isVoucher(acquirable)) {
        return voucherFactory({
            valid: acquirable.valid,
            contentBlocks: acquirable.contentBlocks.map(block => {
                return contentBlockFactory({
                    content: block.content,
                    title: block.title,
                    link: block.link,
                });
            }),
            price: priceFactory(acquirable.price),
            amount: acquirable.amount,
        });
    }
    if (acquirable._type === "GIFTCARD") {
        throw new Error("CANNOT DUPLICATE GIFT CARD");
    }
    if (acquirable._type === "ACCOMMODATION") {
        return accommodationFactory({
            price: priceFactory(acquirable.price),
            amount: acquirable.amount,
            quantities: acquirable.quantities,
            quantityDescriptions: acquirable.quantityDescriptions,
            availability: {
                baseSettings: {
                    ...acquirable.availability.baseSettings,
                },
                availabilityRanges: [],
            },
            inventory: acquirable.inventory.map(duplicateInventory),
            rates: acquirable.rates.map(duplicateRate),

            extrasGroups:
                acquirable.extrasGroups?.map(duplicateExtrasGroup) ?? [],
            contentBlocks: acquirable.contentBlocks.map(block => {
                return contentBlockFactory({
                    content: block.content,
                    title: block.title,
                    link: block.link,
                });
            }),
            hasAccess: false,
        });
    }

    assertNever(acquirable, "Unhandled acquirable type", {
        valueProp: "_type",
    });
};

const duplicateInventory = (
    inventory: AccommodationInventory,
): AccommodationInventory => {
    return accommodationInventoryFactory({
        groupId: inventory.groupId,
        resourcePickerEnabled: inventory.resourcePickerEnabled,
        title: inventory.title,
        description: inventory.description,
    });
};

const duplicateRate = (rate: AccommodationRate): AccommodationRate => {
    return accommodationRateFactory({
        title: rate.title,
        options: rate.options.map(accommodationRateOptionFactory),
        appliesTo: rate.appliesTo,
        conditions:
            rate.conditions?.map(accommodationConditionDuplicateFactory) ?? [],
    });
};

const duplicateExtrasGroup = (extras: ExtrasGroup): ExtrasGroup => {
    return {
        ...extrasGroupsFactory({
            meta: duplicateMetaContent(extras.meta),
            settings: extras.settings,
            products: extras.products,
            conditions: extras.conditions,
        }),
    };
};

const duplicateSku = <S extends Sku>(sku: S, targetProductId: ProductId): S => {
    return skuFactory.from({
        articleNumber: sku.articleNumber,
        meta: duplicateMetaContent(sku.meta),
        productId: targetProductId,
        ...(isBuyable(sku) || isVoucher(sku)
            ? {
                  trackStockBalance: sku.trackStockBalance,
              }
            : {}),
        availableQuantity: sku.availableQuantity,
        acquire: duplicateAcquirable(sku.acquire),
    }) as S;
};

/**
 * Duplicates a product and returns a new one with a new id \
 * NOTE: What sections the product belong to are a relationship based on information saved on the
 * section and therefor not duplicated using this function
 * @param product
 * @param options
 * @returns
 */

const duplicateProduct = (
    product: Product,
    options?: {
        targetProductId?: ProductId;
        targetProviderId?: ProviderId;
        targetTitle?: Translatable;
        /**
         * If true, the new duplicate will be paused, if undefined or false
         * the new duplicate will have the same status as the original product.
         * @type {boolean}
         */
        pause?: boolean;
    },
) => {
    const targetProductId = options?.targetProductId ?? productIdFactory();
    const targetProviderId = options?.targetProviderId ?? product.providerId;
    const targetTitle =
        options?.targetTitle ??
        translation({
            sv: `${
                typeof product.meta.title === "string"
                    ? product.meta.title
                    : (product.meta.title?.sv ?? "[utan namn]")
            } (Kopia)`,
            en:
                typeof product.meta.title === "string"
                    ? undefined
                    : product.meta.title?.en
                      ? `${product.meta.title.en} (Copy)`
                      : undefined,
        });
    const duplicatedMetaContent = duplicateMetaContent(product.meta);
    const duplicatedMeta: MetaContent = {
        ...duplicatedMetaContent,
        title: targetTitle,
    };
    switch (product.acquirableAs) {
        case "ACCOMMODATION": {
            const duplicate = productFactory.from({
                _id: targetProductId,
                _createdAt: now(),
                acquirableAs: product.acquirableAs,
                availability: product.availability,
                meta: duplicatedMeta,
                providerId: targetProviderId,
                skus: product.skus.map(sku =>
                    duplicateSku(sku, targetProductId),
                ) as AccommodationSku[],
                optionalGroupIds: product.optionalGroupIds,
                discountIds: product.discountIds,
                minAcquireQuantity: product.minAcquireQuantity,
                notificationSettings: product.notificationSettings,
                duplicatedFrom: product._id,
                status: options?.pause
                    ? alfredItemStatusFactory({
                          type: "PAUSED",
                      })
                    : product.status,
            });
            return duplicate;
        }
        case "BOOKABLE": {
            const duplicate = productFactory.from({
                _id: targetProductId,
                _createdAt: now(),
                acquirableAs: product.acquirableAs,
                availability: product.availability,
                meta: duplicatedMeta,
                providerId: targetProviderId,
                skus: product.skus.map(sku =>
                    duplicateSku(sku, targetProductId),
                ) as BookableSku[],
                optionalGroupIds: product.optionalGroupIds,
                discountIds: product.discountIds,
                minAcquireQuantity: product.minAcquireQuantity,
                notificationSettings: product.notificationSettings,
                duplicatedFrom: product._id,
                status: options?.pause
                    ? alfredItemStatusFactory({
                          type: "PAUSED",
                      })
                    : product.status,
            });
            return duplicate;
        }
        case "BUYABLE": {
            const duplicate = productFactory.from({
                _id: targetProductId,
                _createdAt: now(),
                acquirableAs: product.acquirableAs,
                availability: product.availability,
                meta: duplicatedMeta,
                providerId: targetProviderId,
                skus: product.skus.map(sku =>
                    duplicateSku(sku, targetProductId),
                ) as BuyableSku[],
                optionalGroupIds: product.optionalGroupIds,
                discountIds: product.discountIds,
                minAcquireQuantity: product.minAcquireQuantity,
                notificationSettings: product.notificationSettings,
                duplicatedFrom: product._id,
                status: options?.pause
                    ? alfredItemStatusFactory({
                          type: "PAUSED",
                      })
                    : product.status,
            });
            return duplicate;
        }
        case "GIFTCARD": {
            const duplicate = productFactory.from({
                _id: targetProductId,
                _createdAt: now(),
                acquirableAs: product.acquirableAs,
                availability: product.availability,
                meta: duplicatedMeta,
                providerId: targetProviderId,
                skus: product.skus.map(sku =>
                    duplicateSku(sku, targetProductId),
                ) as GiftCardSku[],
                optionalGroupIds: product.optionalGroupIds,
                discountIds: product.discountIds,
                minAcquireQuantity: product.minAcquireQuantity,
                notificationSettings: product.notificationSettings,
                duplicatedFrom: product._id,
                status: options?.pause
                    ? alfredItemStatusFactory({
                          type: "PAUSED",
                      })
                    : product.status,
            });
            return duplicate;
        }
        case "VOUCHER": {
            const duplicate = productFactory.from({
                _id: targetProductId,
                _createdAt: now(),
                acquirableAs: product.acquirableAs,
                availability: product.availability,
                meta: duplicatedMeta,
                providerId: targetProviderId,
                skus: product.skus.map(sku =>
                    duplicateSku(sku, targetProductId),
                ) as VoucherSku[],
                optionalGroupIds: product.optionalGroupIds,
                discountIds: product.discountIds,
                minAcquireQuantity: product.minAcquireQuantity,
                notificationSettings: product.notificationSettings,
                duplicatedFrom: product._id,
                status: options?.pause
                    ? alfredItemStatusFactory({
                          type: "PAUSED",
                      })
                    : product.status,
            });
            return duplicate;
        }

        default:
            assertNever(product);
    }
};

export { duplicateProduct, duplicateReference, duplicateRReferenceGroup };
